import {useCallback, type FC} from 'react'
import type {
  ConditionParameters,
  RulesetTarget,
  IncludeExcludeParameters,
  TargetType,
  OrganizationIdConditionMetadata,
  OrganizationIdParameters,
  OrganizationPropertyParameters,
} from '../../../types/rules-types'
import {IncludeExcludeTarget} from './IncludeExcludeTarget'
import {isAllCondition} from '../../../helpers/conditions'
import {OrganizationIdTarget} from './OrganizationIdTarget'
import {Checkbox, FormControl} from '@primer/react'
import {useRuleStrings} from '../../../hooks/use-rule-strings'
import {PUSH_RULESET_TARGET_INFO} from '../../../helpers/constants'
import {OrganizationPropertyTarget} from './OrganizationPropertyTarget'
import type {TargetSource} from '../TargetsPanel'
import type {Enterprise} from '@github-ui/repos-types'

export type OrganizationTargetProps = {
  source: TargetSource
  rulesetId?: number
  readOnly: boolean
  fnmatchHelpUrl?: string
  rulesetTarget: RulesetTarget
  targetType: TargetType
  parameters: ConditionParameters
  metadata?: object
  updateParameters: (parameters: ConditionParameters) => void
  supportsEmuTargeting: boolean
}

export const OrganizationTarget: FC<OrganizationTargetProps> = ({
  source,
  rulesetId,
  readOnly,
  fnmatchHelpUrl,
  rulesetTarget,
  targetType,
  parameters,
  metadata,
  updateParameters,
  supportsEmuTargeting,
}) => {
  const includeEmuAccounts = (parameters as IncludeExcludeParameters | OrganizationIdParameters).include_emu_accounts
  const toggleIncludeEmuAccounts = useCallback(() => {
    updateParameters({
      ...parameters,
      include_emu_accounts: !includeEmuAccounts,
    })
  }, [includeEmuAccounts, parameters, updateParameters])
  const {rulesetOrPolicy} = useRuleStrings()

  const target = isAllCondition(targetType, parameters) ? 'all_orgs' : targetType
  return target === 'all_orgs' && !readOnly ? null : (
    <div>
      {target === 'organization_name' || target === 'all_orgs' ? (
        <IncludeExcludeTarget
          rulesetId={rulesetId}
          readOnly={readOnly}
          rulesetTarget={rulesetTarget}
          fnmatchHelpUrl={fnmatchHelpUrl}
          parameters={parameters as IncludeExcludeParameters}
          panelTitle="Targeting criteria"
          targetType="organization_name"
          updateParameters={updateParameters}
          headerRowText={rulesetTarget === 'push' ? PUSH_RULESET_TARGET_INFO : undefined}
          blankslate={{
            heading: 'No organization targets have been added yet',
          }}
        />
      ) : target === 'organization_id' ? (
        <OrganizationIdTarget
          readOnly={readOnly}
          parameters={parameters as OrganizationIdParameters}
          metadata={metadata as OrganizationIdConditionMetadata | undefined}
          updateParameters={updateParameters}
          headerRowText={rulesetTarget === 'push' ? PUSH_RULESET_TARGET_INFO : undefined}
          blankslate={{
            heading: 'No organization targets have been added yet',
            description: !readOnly ? (
              <>Organization targeting determines which repositories will be protected by this {rulesetOrPolicy}.</>
            ) : undefined,
          }}
        />
      ) : target === 'organization_property' ? (
        <OrganizationPropertyTarget
          enterprise={source as Enterprise}
          readOnly={readOnly}
          parameters={parameters as OrganizationPropertyParameters}
          updateParameters={updateParameters}
          headerRowText={rulesetTarget === 'push' ? PUSH_RULESET_TARGET_INFO : undefined}
          blankslate={{
            heading: 'No organization targets have been added yet',
            description: !readOnly ? (
              <>Organization targeting determines which repositories will be protected by this {rulesetOrPolicy}.</>
            ) : undefined,
          }}
        />
      ) : null}

      {!readOnly && supportsEmuTargeting && (
        <div className={target === 'all_orgs' ? 'pb-3' : 'py-3'}>
          <FormControl>
            <Checkbox checked={includeEmuAccounts || false} onChange={toggleIncludeEmuAccounts} />
            <FormControl.Label>Target all enterprise managed user accounts</FormControl.Label>
            <FormControl.Caption>
              If enabled, this {rulesetOrPolicy} will also apply to all repositories owned by enterprise managed users.
            </FormControl.Caption>
          </FormControl>
        </div>
      )}
    </div>
  )
}
